import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import AdminLayout from "../../layouts/AdminLayout";
import { useToast } from "../../components/ToastContext";
import ChatWidget from "../../components/ChatWidget";
import { getTokenFromCookie } from "../../api/services/authService";
import {
  getTeamScenarioAsPlatformAdmin,
  stopTeamScenarioAsPlatformAdmin,
  getPlatformTeamAnswersStreamUrl,
} from "../../api/services/platformAdminService";
import {
  Monitor,
  Square,
  ShieldOff,
  ShieldAlert,
  ArrowLeft,
  Info,
  Trophy,
  Loader2,
  BarChart3,
  X,
} from "lucide-react";
import TeamLabQuestionsSection from "../CompanyUser/TeamLabQuestionSection";

function extractScenarioIdFromUrl(scenarioUrl) {
  if (typeof scenarioUrl !== "string") return null;
  const match = scenarioUrl.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  );
  return match ? match[0] : null;
}

function parseSseChunk(chunk) {
  const frames = chunk.split("\n\n").filter(Boolean);
  const events = [];
  for (const frame of frames) {
    let event = "message";
    let data = "";
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data = line.slice(5).trim();
    }
    if (data) events.push({ event, data });
  }
  return events;
}

export default function PlatformTeamLabPage() {
  const { labId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const preloadedLabData = location.state;
  const teamId = preloadedLabData?.team_id ?? null;

  // Platform admin is always a read-only monitor here.
  const readOnly = true;

  const [teamScenario, setTeamScenario] = useState(null);
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);
  const [hasScenarioResponse, setHasScenarioResponse] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isScenarioGone, setIsScenarioGone] = useState(false);

  const [frameworkTab, setFrameworkTab] = useState("tksa");
  const [mappingsModalOpen, setMappingsModalOpen] = useState(false);

  const [isStopping, setIsStopping] = useState(false);
  const [isStopped, setIsStopped] = useState(false);

  const [answers] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [questionAnswerData, setQuestionAnswerData] = useState({});

  const scenario = teamScenario?.scenario ?? null;
  const chatScenarioId = useMemo(
    () => extractScenarioIdFromUrl(scenario?.scenario_url),
    [scenario?.scenario_url],
  );
  const rawMetaQuestions = scenario?.metadata?.questions ?? [];
  const rawTopLevelQuestions = teamScenario?.questions ?? [];
  const privateIp = teamScenario?.provisioning?.private_ip ?? null;

  const questions = useMemo(() => {
    const base =
      rawMetaQuestions.length > 0
        ? rawMetaQuestions.map((mq, idx) => {
            const tq = rawTopLevelQuestions[idx] ?? {};
            return {
              ...mq,
              is_answered: tq.is_answered ?? false,
              locked: tq.locked ?? false,
              answer_text: tq.answer_text ?? null,
              submitted_by_username: tq.submitted_by_username ?? null,
              submitted_at: tq.submitted_at ?? null,
              answer_length: tq.answer_length ?? null,
            };
          })
        : rawTopLevelQuestions;

    return base.map((q, idx) => {
      const live = questionAnswerData[idx];
      if (!live) return q;
      return {
        ...q,
        locked: true,
        is_answered: true,
        answer_text: live.answer_text ?? q.answer_text,
        submitted_by_username:
          live.submitted_by_username ?? q.submitted_by_username,
        submitted_at: live.submitted_at ?? q.submitted_at,
      };
    });
  }, [rawMetaQuestions, rawTopLevelQuestions, questionAnswerData]);

  const tksaBuckets = useMemo(() => {
    const TKSA_ID_REGEX = /^([TKSA])(\d+)/i;
    const cleanId = (raw) => {
      if (typeof raw !== "string") return raw;
      const m = raw.trim().match(TKSA_ID_REGEX);
      return m ? `${m[1].toUpperCase()}${m[2]}` : raw.trim();
    };
    const sets = {
      Tasks: new Set(),
      Skills: new Set(),
      Abilities: new Set(),
      Knowledge: new Set(),
      Other: new Set(),
    };
    questions.forEach((q) => {
      (q.framework_alignment?.tksa_ids || []).forEach((raw) => {
        const id = cleanId(raw);
        const prefix = id?.[0]?.toUpperCase();
        if (prefix === "T") sets.Tasks.add(id);
        else if (prefix === "S") sets.Skills.add(id);
        else if (prefix === "A") sets.Abilities.add(id);
        else if (prefix === "K") sets.Knowledge.add(id);
        else sets.Other.add(id);
      });
    });
    return Object.entries(sets)
      .map(([label, set]) => ({ label, items: [...set].sort() }))
      .filter((b) => b.items.length > 0);
  }, [questions]);

  const labTitle =
    scenario?.title ?? preloadedLabData?.title ?? "Lab not found";
  const labDescription =
    scenario?.description ?? preloadedLabData?.description ?? "";

  const mitreAttack = scenario?.metadata?.mitre_attack || null;
  const hasTksaData = tksaBuckets.length > 0;
  const hasMitreData =
    !!mitreAttack &&
    Object.values(mitreAttack).some((v) => Array.isArray(v) && v.length > 0);
  const activeFrameworkTab =
    frameworkTab === "mitre"
      ? hasMitreData
        ? "mitre"
        : "tksa"
      : hasTksaData
        ? "tksa"
        : "mitre";

  // ── resync helper (re-fetch detail, merge answered questions) ──
  const resyncFromServer = useCallback(async () => {
    if (!labId) return;
    try {
      const data = await getTeamScenarioAsPlatformAdmin({
        scenarioId: labId,
        teamId,
      });
      setTeamScenario(data);

      const newAnswerData = {};
      (data.questions ?? []).forEach((q) => {
        if (q.is_answered || q.locked) {
          newAnswerData[q.question_index] = {
            answer_text: q.answer_text,
            submitted_by_username: q.submitted_by_username,
            submitted_at: q.submitted_at,
          };
        }
      });
      setQuestionAnswerData((prev) => ({ ...prev, ...newAnswerData }));
      setSubmitted((prev) => {
        const next = { ...prev };
        Object.keys(newAnswerData).forEach((idx) => {
          if (!next[idx]) next[idx] = "correct";
        });
        return next;
      });
    } catch (err) {
      console.error("Failed to resync team scenario:", err);
    }
  }, [labId, teamId]);

  // ── initial load ──
  useEffect(() => {
    if (!labId) return;
    let isActive = true;

    const load = async () => {
      try {
        setHasScenarioResponse(false);
        setIsLoadingScenario(true);
        setLoadError("");

        const data = await getTeamScenarioAsPlatformAdmin({
          scenarioId: labId,
          teamId,
        });
        if (!isActive) return;

        setTeamScenario(data);

        const initialAnswerData = {};
        (data.questions ?? []).forEach((q) => {
          if (q.is_answered || q.locked) {
            initialAnswerData[q.question_index] = {
              answer_text: q.answer_text,
              submitted_by_username: q.submitted_by_username,
              submitted_at: q.submitted_at,
            };
          }
        });
        setQuestionAnswerData(initialAnswerData);

        const initialSubmitted = {};
        Object.keys(initialAnswerData).forEach((idx) => {
          initialSubmitted[idx] = "correct";
        });
        setSubmitted(initialSubmitted);

        setHasScenarioResponse(true);
      } catch (err) {
        console.error("Failed to load team scenario:", err);
        if (isActive) {
          if (err?.status === 404 || err?.response?.status === 404) {
            setIsScenarioGone(true);
          } else {
            setLoadError(err?.message ?? "Failed to load scenario details.");
          }
          setHasScenarioResponse(true);
        }
      } finally {
        if (isActive) setIsLoadingScenario(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [labId, teamId]);

  // ── SSE stream (platform-admin variant) via fetch ──
  // GET /api/v1/team-scenario/admin/{scenario_id}/answers/stream?team_id=
  // Emits `answer_locked`; on each, re-fetch detail to reveal answer text.
  useEffect(() => {
    if (!labId || !teamId || isScenarioGone) return;

    const streamUrl = getPlatformTeamAnswersStreamUrl({
      scenarioId: labId,
      teamId,
    });
    let reconnectTimerId = null;
    let stopped = false;
    let currentAbortController = null;

    const startStream = async () => {
      currentAbortController = new AbortController();
      const accessToken = getTokenFromCookie("access_token") ?? "";
      if (stopped) return;
      try {
        const response = await fetch(streamUrl, {
          headers: {
            Accept: "text/event-stream",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          signal: currentAbortController.signal,
        });

        if (!response.ok) {
          if (response.status === 404) {
            setIsScenarioGone(true);
            stopped = true;
            return;
          }
          if (response.status === 401 || response.status === 403) {
            if (!stopped) reconnectTimerId = setTimeout(startStream, 5000);
            return;
          }
          throw new Error(`SSE response ${response.status}`);
        }

        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .getReader();

        let buffer = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += value;

          const boundary = buffer.lastIndexOf("\n\n");
          if (boundary === -1) continue;
          const complete = buffer.slice(0, boundary + 2);
          buffer = buffer.slice(boundary + 2);

          const sseEvents = parseSseChunk(complete);
          for (const { event, data } of sseEvents) {
            if (event === "keepalive") continue;
            if (event === "answer_locked") {
              try {
                const payload = JSON.parse(data);
                const idx = payload?.question_index;
                if (typeof idx !== "number") continue;

                // Optimistically mark locked, then resync to fill answer text.
                setQuestionAnswerData((prev) => ({
                  ...prev,
                  [idx]: {
                    answer_text: prev[idx]?.answer_text ?? null,
                    submitted_by_username:
                      payload.submitted_by_username ?? null,
                    submitted_at: payload.submitted_at ?? null,
                  },
                }));
                setSubmitted((prev) =>
                  prev[idx] === "correct"
                    ? prev
                    : { ...prev, [idx]: "correct" },
                );
                resyncFromServer();
              } catch {
                /* malformed payload — ignore */
              }
            }
          }
        }

        if (!stopped) reconnectTimerId = setTimeout(startStream, 3000);
      } catch (err) {
        if (err?.name === "AbortError") return;
        resyncFromServer();
        if (!stopped) reconnectTimerId = setTimeout(startStream, 3000);
      }
    };

    startStream();

    return () => {
      stopped = true;
      currentAbortController?.abort();
      if (reconnectTimerId) clearTimeout(reconnectTimerId);
    };
  }, [labId, teamId, isScenarioGone, resyncFromServer]);

  // ── poll only to detect stopped/expired (404) ──
  useEffect(() => {
    if (!labId || isScenarioGone) return;
    const checkIfGone = async () => {
      try {
        await getTeamScenarioAsPlatformAdmin({ scenarioId: labId, teamId });
      } catch (err) {
        if (err?.status === 404 || err?.response?.status === 404) {
          setIsScenarioGone(true);
        }
      }
    };
    const id = setInterval(checkIfGone, 30000);
    return () => clearInterval(id);
  }, [labId, teamId, isScenarioGone]);

  const handleStopChallenge = async () => {
    if (!labId) {
      showToast("Scenario ID is missing.", "error");
      return;
    }
    try {
      setIsStopping(true);
      await stopTeamScenarioAsPlatformAdmin({ scenarioId: labId, teamId });
      setIsStopped(true);
      showToast("Team challenge stopped.", "success");
    } catch (error) {
      showToast(error.message || "Failed to stop team challenge.", "error");
    } finally {
      setIsStopping(false);
    }
  };

  const totalQuestions = questions.length;
  const showQuestionsLoader =
    isLoadingScenario || (!hasScenarioResponse && !loadError);
  const allQuestionsAnswered =
    totalQuestions > 0 &&
    questions.every((question) => question.is_answered || question.locked);
  const showChallengeCompleted =
    hasScenarioResponse && !isLoadingScenario && allQuestionsAnswered;

  if (isScenarioGone) {
    return (
      <AdminLayout>
        <div className="w-full max-w-362.5 mx-auto px-3 sm:px-6 py-6 sm:py-10 min-h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 text-center max-w-md">
            <div className="flex h-16 w-16 items-center justify-center rounded-md border border-(--color-border) bg-transparent">
              <ShieldOff className="h-7 w-7 text-(--color-text-muted)" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-(--color-text-primary)">
                This challenge is no longer active
              </h2>
              <p className="text-sm text-(--color-text-muted) leading-relaxed">
                The team challenge has been stopped or has expired. Head back to
                see other active challenges across the platform.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin/team-challenges")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-(--color-primary) bg-(--color-primary) text-(--color-text-on-primary) text-sm font-semibold transition-all hover:opacity-90"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Team Challenges
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="w-full max-w-362.5 mx-auto px-3 sm:px-6 py-6 sm:py-10 space-y-6 min-h-[calc(100vh-4rem)]">
        <div className="space-y-4">
          <div className="w-full">
            <p className="text-xs uppercase tracking-[0.25em] text-(--color-primary)/80">
              Team Challenge
            </p>

            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-(--color-border) bg-transparent text-xs text-(--color-text-muted)">
              <Info className="h-3.5 w-3.5 shrink-0" />
              Read-only monitoring view — platform admin. Answers update live.
            </div>

            {isLoadingScenario && !teamScenario && !preloadedLabData ? (
              <div className="space-y-3 mt-1">
                <div className="h-9 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-lg animate-pulse bg-size-[200%_100%] w-96 max-w-full" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4 flex-wrap mt-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-(--color-text-primary) leading-tight [text-shadow:0_0_12px_var(--color-shadow-primary)]/30">
                    {labTitle}
                  </h1>
                  {!isLoadingScenario &&
                    hasScenarioResponse &&
                    (hasTksaData || hasMitreData) && (
                      <button
                        type="button"
                        onClick={() => setMappingsModalOpen(true)}
                        className="shrink-0 inline-flex items-center gap-2 rounded-md border border-(--color-primary)/30 bg-(--color-primary)/5 px-4 py-2 text-xs sm:text-sm font-semibold text-(--color-primary) hover:bg-(--color-primary)/10 hover:shadow-[0_0_15px_rgba(0,255,0,0.2)] transition-all"
                      >
                        <BarChart3 className="w-4 h-4" />
                        View TKSA Alignment and MITRE Mapping
                      </button>
                    )}
                  {(isLoadingScenario ||
                    (!hasScenarioResponse && !loadError)) && (
                    <div className="shrink-0 h-9 w-64 max-w-full rounded-md bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 animate-pulse bg-size-[200%_100%]" />
                  )}
                </div>
                <p className="text-(--color-text-muted) dark:text-gray-400 text-sm max-w-3xl mt-3">
                  Monitoring this team challenge. You can stop it for the team
                  below.
                </p>

                {showChallengeCompleted && (
                  <div
                    className="mt-5 relative overflow-hidden rounded-md"
                    style={{
                      border: "1px solid var(--color-border-medium)",
                      padding: "18px 22px 18px 24px",
                    }}
                  >
                    <div className="relative flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3.5">
                        <div
                          className="relative shrink-0 w-11 h-11 rounded-md flex items-center justify-center"
                          style={{
                            border: "1px solid var(--color-border-dark)",
                            background: "var(--color-primary-50)",
                          }}
                        >
                          <Trophy
                            className="w-5 h-5"
                            style={{ color: "var(--color-primary)" }}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span
                            className="text-sm font-bold uppercase"
                            style={{ color: "var(--color-primary)" }}
                          >
                            Challenge completed
                          </span>
                          <span
                            className="text-xs tracking-wide"
                            style={{ color: "var(--color-text-light-green)" }}
                          >
                            This team solved every question.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Framework & ATT&CK Mappings modal */}
                {mappingsModalOpen &&
                  !isLoadingScenario &&
                  hasScenarioResponse &&
                  (hasTksaData || hasMitreData) && (
                    <div
                      className="fixed inset-0 z-50 flex items-center justify-center p-4"
                      role="dialog"
                      aria-modal="true"
                    >
                      <div
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                        onClick={() => setMappingsModalOpen(false)}
                      />
                      <div className="relative z-10 w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden rounded-md border border-(--color-primary)/30 bg-(--color-bg-card) shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-(--color-border)">
                          <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-(--color-primary)" />
                            <span className="text-sm font-semibold text-(--color-text-primary)">
                              TKSA Alignment and MITRE Mapping
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setMappingsModalOpen(false)}
                            aria-label="Close"
                            className="rounded-md p-1.5 text-(--color-text-muted) hover:bg-white/5 hover:text-(--color-primary) transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                          <div className="bg-transparent">
                            <div className="flex flex-wrap border-b border-(--color-primary)/15">
                              {hasTksaData && (
                                <button
                                  type="button"
                                  onClick={() => setFrameworkTab("tksa")}
                                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                    activeFrameworkTab === "tksa"
                                      ? "text-(--color-primary) border-(--color-primary) bg-(--color-primary)/5"
                                      : "text-(--color-text-muted) border-transparent hover:text-(--color-text-primary) hover:bg-(--color-primary)/5"
                                  }`}
                                >
                                  <BarChart3 className="w-4 h-4" />
                                  TKSA Alignment
                                  <span className="text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded-md bg-(--color-primary)/10 text-(--color-text-light-green)">
                                    {tksaBuckets.reduce(
                                      (s, b) => s + b.items.length,
                                      0,
                                    )}
                                  </span>
                                </button>
                              )}
                              {hasMitreData && (
                                <button
                                  type="button"
                                  onClick={() => setFrameworkTab("mitre")}
                                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                    activeFrameworkTab === "mitre"
                                      ? "text-(--color-primary) border-(--color-primary) bg-(--color-primary)/5"
                                      : "text-(--color-text-muted) border-transparent hover:text-(--color-text-primary) hover:bg-(--color-primary)/5"
                                  }`}
                                >
                                  <ShieldAlert className="w-4 h-4" />
                                  MITRE ATT&amp;CK
                                  <span className="text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded-md bg-(--color-primary)/10 text-(--color-text-light-green)">
                                    {Object.values(mitreAttack)
                                      .filter(Array.isArray)
                                      .reduce((s, a) => s + a.length, 0)}
                                  </span>
                                </button>
                              )}
                            </div>

                            {activeFrameworkTab === "tksa" && hasTksaData && (
                              <div className="px-4 pb-4 pt-4 flex flex-col gap-4">
                                {tksaBuckets.map((bucket) => {
                                  const colorMap = {
                                    Tasks: {
                                      dot: "#378ADD",
                                      bg: "rgba(55,138,221,0.15)",
                                      border: "rgba(55,138,221,0.3)",
                                      text: "#85B7EB",
                                    },
                                    Skills: {
                                      dot: "#639922",
                                      bg: "rgba(99,153,34,0.15)",
                                      border: "rgba(99,153,34,0.3)",
                                      text: "#97C459",
                                    },
                                    Abilities: {
                                      dot: "#7F77DD",
                                      bg: "rgba(127,119,221,0.15)",
                                      border: "rgba(127,119,221,0.3)",
                                      text: "#AFA9EC",
                                    },
                                    Knowledge: {
                                      dot: "#BA7517",
                                      bg: "rgba(186,117,23,0.15)",
                                      border: "rgba(186,117,23,0.3)",
                                      text: "#EF9F27",
                                    },
                                    Other: {
                                      dot: "#888888",
                                      bg: "rgba(128,128,128,0.15)",
                                      border: "rgba(128,128,128,0.3)",
                                      text: "#AAAAAA",
                                    },
                                  };
                                  const c = colorMap[bucket.label] ?? {
                                    dot: "#888",
                                    bg: "rgba(128,128,128,0.15)",
                                    border: "rgba(128,128,128,0.3)",
                                    text: "#aaa",
                                  };
                                  return (
                                    <div key={bucket.label}>
                                      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-(--color-text-muted) mb-2 flex items-center gap-2">
                                        {bucket.label}
                                        <span className="font-mono tabular-nums">
                                          {bucket.items.length}
                                        </span>
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {bucket.items.map((item) => (
                                          <span
                                            key={item}
                                            className="inline-flex items-center gap-1.5 text-xs font-mono font-medium px-2.5 py-1 rounded-md border"
                                            style={{
                                              background: c.bg,
                                              color: c.text,
                                              borderColor: c.border,
                                            }}
                                          >
                                            <span
                                              className="w-1.5 h-1.5 rounded-full shrink-0"
                                              style={{ background: c.dot }}
                                            />
                                            {item}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {activeFrameworkTab === "mitre" && hasMitreData && (
                              <div className="px-4 pb-4 pt-4 flex flex-col gap-4">
                                {Object.entries(mitreAttack)
                                  .filter(
                                    ([, items]) =>
                                      Array.isArray(items) && items.length > 0,
                                  )
                                  .map(([key, items], sectionIdx) => {
                                    const palette = [
                                      {
                                        dot: "#7F77DD",
                                        bg: "rgba(127,119,221,0.12)",
                                        border: "rgba(127,119,221,0.3)",
                                        text: "#AFA9EC",
                                      },
                                      {
                                        dot: "#22C55E",
                                        bg: "rgba(34,197,94,0.12)",
                                        border: "rgba(34,197,94,0.3)",
                                        text: "#4ADE80",
                                      },
                                      {
                                        dot: "#378ADD",
                                        bg: "rgba(55,138,221,0.12)",
                                        border: "rgba(55,138,221,0.3)",
                                        text: "#85B7EB",
                                      },
                                      {
                                        dot: "#BA7517",
                                        bg: "rgba(186,117,23,0.12)",
                                        border: "rgba(186,117,23,0.3)",
                                        text: "#EF9F27",
                                      },
                                      {
                                        dot: "#EF4444",
                                        bg: "rgba(239,68,68,0.12)",
                                        border: "rgba(239,68,68,0.3)",
                                        text: "#FCA5A5",
                                      },
                                    ];
                                    const c =
                                      palette[sectionIdx % palette.length];
                                    return (
                                      <div key={key}>
                                        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-(--color-text-muted) mb-2">
                                          {key.replace(/[_-]/g, " ")}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                          {items.map((item) => (
                                            <span
                                              key={item}
                                              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border"
                                              style={{
                                                background: c.bg,
                                                color: c.text,
                                                borderColor: c.border,
                                              }}
                                            >
                                              <span
                                                className="w-1.5 h-1.5 rounded-full shrink-0"
                                                style={{ background: c.dot }}
                                              />
                                              {item}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>

        {/* Overview content */}
        <div className="bg-transparent border border-(--color-primary)/30 rounded-md overflow-hidden">
          <div className="p-3 sm:p-6 min-h-[50vh]">
            <div className="space-y-6">
              <div className="border border-(--color-primary)/30 rounded-md p-3 sm:p-6 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-(--color-primary)/10 rounded-md shrink-0">
                      <Monitor className="h-5 w-5 text-(--color-primary)" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-(--color-text-primary)">
                          Team Machine Status
                        </p>
                        <span className="px-3 py-1 rounded-md text-xs font-semibold bg-(--color-primary)/20 border border-(--color-primary)/40 text-(--color-primary)">
                          Running
                        </span>
                        {privateIp && (
                          <span className="px-3 py-1 text-xs text-(--color-text-muted) dark:text-gray-400 font-mono">
                            <b>SCENARIO_IP</b> {privateIp}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-(--color-text-muted) dark:text-gray-400 mt-1">
                        Shared team machine. Monitoring only.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 sm:shrink-0">
                    {!isStopped ? (
                      <button
                        onClick={handleStopChallenge}
                        disabled={isStopping}
                        className="w-full sm:w-auto px-6 py-3 bg-transparent border border-orange-500/50 text-orange-400 font-semibold rounded-md hover:bg-orange-500/10 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:border-gray-500/50 disabled:text-gray-500 disabled:hover:bg-transparent"
                      >
                        {isStopping ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                        {isStopping ? "Stopping..." : "Stop Team Challenge"}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-2 px-6 py-3 text-sm text-(--color-text-muted) border border-(--color-border) rounded-md">
                        <ShieldAlert className="h-4 w-4" />
                        Team Challenge Stopped
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <TeamLabQuestionsSection
                showQuestionsLoader={showQuestionsLoader}
                totalQuestions={totalQuestions}
                questions={questions}
                answers={answers}
                submitted={submitted}
                submittingQuestions={{}}
                attempts={{}}
                setAnswers={() => {}}
                setSubmitted={setSubmitted}
                handleSubmitAnswer={() => false}
                readOnly={readOnly}
                onAttemptQuestion={() => {}}
                missionTitle={labTitle}
                missionDescription={labDescription}
                difficulty={
                  scenario?.difficulty || preloadedLabData?.difficulty
                }
              />
            </div>
          </div>
        </div>
      </div>
      <ChatWidget scenarioId={chatScenarioId} />
    </AdminLayout>
  );
}
